import SignOutButton from '../../components/Button/SignOutButton'
import FaceIcon from '@mui/icons-material/Face'
import { useState, useEffect } from 'react'
import { doc, updateDoc, getDoc } from 'firebase/firestore'
import UserInfoModule from './UserInfo.module.scss'
import * as React from 'react'
import { useNavigate } from 'react-router-dom'
import { _fields_of_user_data_in_mypage } from '../../localConstant'
import { auth, db, functions } from '../../firebase'
import { httpsCallable } from 'firebase/functions'
import { getUserDataFromMingaku, updateUserData } from '../../utils/getUserDataFromMingaku'
import Textarea from '@mui/joy/Textarea'
import Input from '@mui/joy/Input'
import FormControl from '@mui/material/FormControl'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import LoadingGrid from '../LoadingGrid/LoadingGrid'
import LangToggleBox from '../LangToggleBox/LangToggleBox'

const CustomInputArea = ({ field, userData, handleUserDataChange }) => {
  const defaultTextArea = (
    <Textarea
      minRows={field.textareaRows}
      className={field.editingPermission ? UserInfoModule['inputbox-enabled'] : UserInfoModule['inputbox-disabled']}
      sx={{ width: '80%' }}
      value={userData[field.value]}
      disabled={!field.editingPermission}
      onChange={(e) => handleUserDataChange({ [field.value]: e.target.value })}
    />
  )
  const type = field.dataType ?? ''
  switch (type) {
    case 'string':
      return defaultTextArea

    case 'number':
      return (
        <FormControl sx={{ width: '80%' }}>
          <Input
            type={'number'}
            value={userData[field.value] ?? 0}
            onChange={(e) => handleUserDataChange({ [field.value]: e.target.value })}
          />
        </FormControl>
      )

    case 'select':
      return (
        <FormControl sx={{ width: '80%' }} size="small">
          <Select
            labelId="select-label"
            id="select-property"
            value={userData[field.value] ?? ''}
            label=""
            onChange={(e) => handleUserDataChange({ [field.value]: e.target.value })}
          >
            {(field.choices ? field.choices : []).map((choice, i) => (
              <MenuItem key={`${choice}-i`} value={choice ?? ''}>
                {choice ?? ''}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )

    default:
      return defaultTextArea
  }
}

const LangSetting = () => {
  return (
    <div className={UserInfoModule[`lang-setting-container`]}>
      <p>使用言語</p>
      <LangToggleBox />
    </div>
  )
}

function UserInfo() {
  const buttonClassName = window.location.pathname === '/mypage' ? 'button' : 'button-interview'
  const [role, setRole] = useState('')
  const [loading, setLoading] = useState(true)
  const [canChangePassword, setCanChangePassword] = useState(false)
  const navigate = useNavigate()
  const [editing, setEditing] = useState(false)
  const handleUserDataChange = (updates) => {
    setUserData((prevData) => ({
      ...prevData,
      ...updates
    }))
  }

  const initialUserData = _fields_of_user_data_in_mypage.reduce((acc, field) => {
    acc[field.value] = ''
    return acc
  }, {})
  const [userData, setUserData] = useState(initialUserData)

  const formatCustomProperties = (customProperties) => {
    if (!customProperties || !Array.isArray(customProperties) || !customProperties.length) return []
    return customProperties.map((p) => ({
      ...p,
      editableByStudent: p.editableByStudent ?? false,
      showToStudent: p.showToStudent ?? false
    }))
  }

  useEffect(() => {
    const getUser = async () => {
      const userDocRef = doc(db, 'users', auth.currentUser.uid)
      const userinfo = await getDoc(userDocRef)
      let role = ''
      if (!!userinfo.data().studentDocId) {
        role = 'student'
      } else if (userinfo.data().role === 'tutor') {
        role = 'tutor'
      } else {
        role = 'admin'
      }
      setRole(role)
      if (role === 'student') {
        const { schoolDocId, studentDocId } = userinfo.data()
        const getMingakuSchoolInfo = httpsCallable(functions, 'get_school_info_of_mingaku')
        const getSchoolsInfo = httpsCallable(functions, 'get_schools_info')
        const [userData, mingakuSchoolConfig, mingakuSchoolData] = await Promise.all([
          updateUserData(userinfo),
          getMingakuSchoolInfo({ schoolId: schoolDocId }),
          getSchoolsInfo({ schoolIds: [schoolDocId] })
        ])

        const rawData = mingakuSchoolData.data.schoolInfo[0]?.customProperties ?? []
        const customProperties = formatCustomProperties(rawData)
        const targetProperties = customProperties
          .filter((p) => p.showToStudent)
          .map((p) => ({
            displayName: p.title ?? '不明なプロパティ',
            value: p.id,
            type: 'student',
            editingPermission: p.editableByStudent ?? false,
            source: 'mingaku',
            textareaRows: 1,
            isCustomProperty: true,
            dataType: p.type, // string or number or select
            choices: p.choices ?? []
          }))

        setStudentFilteredFields((prev) => {
          return [...prev, ...targetProperties]
        })

        const { properties: _properties, mingakuData, schoolAiData } = userData
        const combinedData = { ...mingakuData, ...schoolAiData, studentDocId }

        const properties = targetProperties.reduce((result, target) => {
          const found = _properties.find((_p) => _p.id === target.value)
          if (found) {
            result.push({ id: found.id, value: found.value })
          } else {
            result.push({ id: target.value, value: '' })
          }
          return result
        }, [])

        properties.forEach((p) => {
          combinedData[p.id] = p.value
        })

        const { configData } = mingakuSchoolConfig.data
        const allowStudentsToChangePassword = configData ? configData.allowStudentsToChangePassword : false
        setCanChangePassword(allowStudentsToChangePassword)
        handleUserDataChange(combinedData)
      } else if (role === 'tutor') {
        const { mingakuData, schoolAiData } = await updateUserData(userinfo)
        handleUserDataChange({ ...mingakuData, ...schoolAiData })
      } else {
        const { mingakuUserId, email } = userinfo.data()
        if (mingakuUserId === undefined) {
          try {
            const getMingakuUserInfo = httpsCallable(functions, 'get_mingaku_user_info_by_email')
            const { data } = await getMingakuUserInfo({ email })
            try {
              navigate(`/management?role=admin&muid=${data.userAuthData.uid}`)
            } catch (e) {
              alert(
                'このアカウントはみんがくアカウントと連携されてません。連携を完了させるために、みんがく管理画面のスクールAI管理からスクールAIにアクセスしてください。(全ての機能を利用するためには、みんがくアカウントとの連携が必要です。)'
              )
            }
          } catch (e) {
            alert(
              'このアカウントはみんがくアカウントと連携されてません。連携を完了させるために、みんがく管理画面のスクールAI管理からスクールAIにアクセスしてください。(全ての機能を利用するためには、みんがくアカウントとの連携が必要です。)'
            )
          }
        } else {
          const mingakuUserData = await getUserDataFromMingaku(userinfo)
          handleUserDataChange(mingakuUserData)
        }
      }

      setLoading(false)
    }
    getUser()
    // eslint-disable-next-line
  }, [])

  const updateCustomProperties = async (data) => {
    const call = httpsCallable(functions, 'update_custom_properties')
    const res = await call(data)
    if (!res.data.success) {
      throw new Error(res.data.message ?? 'unexpected error occurred in the process of updating custom property')
    }
  }

  const onSave = async (userId) => {
    if (role !== 'student') return
    const userDocRef = doc(db, 'users', userId)
    const { userDataUpdates, customPropertyUpdates } = studentFilteredFields
      .filter((field) => field.editingPermission)
      .reduce(
        (acc, field) => {
          const targetKey = field.isCustomProperty ? 'customPropertyUpdates' : 'userDataUpdates'
          acc[targetKey][field.value] = userData[field.value] || ''
          return acc
        },
        { userDataUpdates: {}, customPropertyUpdates: {} }
      )

    const formattedProperties = Object.entries(customPropertyUpdates).map(([id, value]) => ({ id, value }))

    try {
      setLoading(true)
      await Promise.all([
        updateDoc(userDocRef, userDataUpdates),
        formattedProperties.length
          ? updateCustomProperties({ studentDocId: userData.studentDocId, properties: formattedProperties })
          : Promise.resolve()
      ])
      alert('変更を保存しました。')
    } catch (e) {
      console.error(e)
      alert('一部情報の変更に失敗しました。もう一度やり直してください。' + (e.message && '\n' + e.message))
    } finally {
      setEditing(false)
      setLoading(false)
    }
  }
  const [count, setCount] = useState(0)
  const increment = () => {
    setCount(count + 1)
    if (count >= 5) {
      // navigate('/management') 顔5回クリックで管理画面に遷移
    }
  }

  const [tutorFilteredFields, _] = useState(
    _fields_of_user_data_in_mypage.filter((field) => field.type === 'all' || field.type === 'tutor')
  )
  const [studentFilteredFields, setStudentFilteredFields] = useState(
    _fields_of_user_data_in_mypage.filter(
      (field) => (field.type === 'all' || field.type === 'student') && field.value !== 'characteristic'
    )
  )

  const moveToResetPassWord = () => {
    const projectId = process.env.REACT_APP_FIREBASE_PROJECT_ID
    if (!projectId) return
    if (projectId === 'nande-9773c') {
      //dev
      window.location.href = 'http://localhost:8080/reset_password/school_ai'
    } else if (projectId === 'school-ai-stg') {
      //stg
      window.location.href = 'https://mingaku-st.web.app/reset_password/school_ai'
    } else if (projectId === 'chatgpt-teacher') {
      //prod
      window.location.href = 'https://app.mingaku.net/reset_password/school_ai'
    } else {
      return
    }
  }

  return loading ? (
    <LoadingGrid />
  ) : (
    <div className={UserInfoModule.container}>
      {editing ? (
        <div>
          <div className={UserInfoModule['user-info']}>
            <FaceIcon className={UserInfoModule.icon} />
            <p className={UserInfoModule.p}>{auth.currentUser.displayName}</p>
            {studentFilteredFields.map((field) => (
              <div key={field.value} className={UserInfoModule.input}>
                <p className={UserInfoModule.input}>{field.displayName}：</p>
                <CustomInputArea field={field} userData={userData} handleUserDataChange={handleUserDataChange} />
              </div>
            ))}
          </div>
          <button className={UserInfoModule[buttonClassName]} onClick={() => onSave(auth.currentUser.uid)}>
            保存
          </button>
          <button className={UserInfoModule[buttonClassName]} onClick={() => setEditing(false)}>
            戻る
          </button>
        </div>
      ) : role === 'student' ? (
        <div>
          <div className={UserInfoModule['user-info']}>
            <FaceIcon onClick={increment} className={UserInfoModule.icon} />
            <p>{auth.currentUser.displayName}</p>
            {studentFilteredFields.map((field) => (
              <div key={field.value} className={UserInfoModule.input}>
                <p className={UserInfoModule.input}>{field.displayName}：</p>
                <div className={UserInfoModule.name}>
                  {userData[field.value] ? userData[field.value] : userData[field.value] === 0 ? 0 : '-'}
                </div>
              </div>
            ))}
            <LangSetting />
          </div>
          <div className={UserInfoModule['button-container']}>
            <button className={UserInfoModule[buttonClassName]} onClick={() => setEditing(true)}>
              基本情報を変更する
            </button>
            {canChangePassword && (
              <button className={UserInfoModule[buttonClassName]} onClick={() => moveToResetPassWord()}>
                ログインパスワード
                <br />
                変更
              </button>
            )}
          </div>
          <SignOutButton loginPath={`${role === 'tutor' ? '/login' : '/login_for_student'}`} />
        </div>
      ) : (
        <div>
          <div className={UserInfoModule['user-info']}>
            <FaceIcon onClick={increment} className={UserInfoModule.icon} />
            <p>{auth.currentUser.displayName}</p>
            {tutorFilteredFields.map((field) => (
              <div key={field.value} className={UserInfoModule.input}>
                <p className={UserInfoModule.input}>{field.displayName}：</p>
                <div className={UserInfoModule.name}>{userData[field.value]}</div>
              </div>
            ))}
            <LangSetting />
          </div>
          {/* <button className={UserInfoModule[buttonClassName]} onClick={() => setEditing(true)}>
              基本情報を変更する
            </button> */}
          <SignOutButton loginPath={`${role === 'tutor' ? '/login' : '/login_for_student'}`} />
        </div>
      )}
      {/* <p>メールアドレス認証：{auth.currentUser.emailVerified ? '認証済み✅' : '未認証❌'}</p> */}
    </div>
  )
}

export default UserInfo
